import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, DollarSign, Percent, BarChart3, HelpCircle, Loader2, AlertTriangle } from 'lucide-react';
import { analysisAPI } from '../services/api';
import { MetricTooltip } from './MetricTooltip';

// Score badge component
const MetricBadge = ({ value, type, showDelta = false, delta = null }) => {
  const getColorClass = () => {
    if (type === 'pe') {
      // Lower P/E is better
      if (value < 10) return 'text-emerald-400';
      if (value < 15) return 'text-emerald-400';
      if (value < 20) return 'text-amber-400';
      return 'text-red-400';
    }
    if (type === 'roe') {
      // Higher ROE is better
      if (value > 15) return 'text-emerald-400';
      if (value > 10) return 'text-emerald-400';
      if (value > 5) return 'text-amber-400';
      return 'text-red-400';
    }
    if (type === 'spread') {
      // Positive spread is better
      if (value > 5) return 'text-emerald-400';
      if (value > 2) return 'text-emerald-400';
      if (value > 0) return 'text-amber-400';
      return 'text-red-400';
    }
    if (type === 'score') {
      if (value >= 75) return 'text-emerald-400';
      if (value >= 50) return 'text-amber-400';
      return 'text-red-400';
    }
    return 'text-white';
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className={`font-bold ${getColorClass()}`}>
        {value !== null && value !== undefined ? value.toFixed(1) : '—'}
      </span>
      {showDelta && delta !== null && Math.abs(delta) > 0.1 && (
        <span className={`text-xs ${delta > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
          ({delta > 0 ? '+' : ''}{delta.toFixed(1)}%)
        </span>
      )}
    </div>
  );
};

// Metric row component
const MetricRow = ({ label, tooltipKey, icon: Icon, nominal, real, unit = '', type }) => {
  const delta = nominal && real ? ((real - nominal) / nominal) * 100 : null;
  
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <MetricTooltip metric={tooltipKey}>
        <div className="flex items-center gap-2 cursor-help">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <span className="text-sm text-gray-400">{label}</span>
          <HelpCircle className="w-3 h-3 text-gray-600" />
        </div>
      </MetricTooltip>
      
      <div className="flex items-center gap-4">
        {nominal !== null && nominal !== undefined && (
          <div className="text-right">
            <span className="text-xs text-gray-500">Nominal</span>
            <div className="text-sm text-gray-400">{nominal.toFixed(1)}{unit}</div>
          </div>
        )}
        <div className="text-right">
          <span className="text-xs text-emerald-500">Réel</span>
          <MetricBadge value={real} type={type} showDelta={true} delta={delta} />
        </div>
      </div>
    </div>
  );
};

// Main widget component
const RealMetricsWidget = ({ market, symbol, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await analysisAPI.getRealMetrics(market, symbol);
        setData(result);
      } catch (err) {
        console.error('Error fetching real metrics:', err);
        setError(err.response?.status === 401 ? 'Connexion requise' : 'Données non disponibles');
      } finally {
        setLoading(false);
      }
    };
    
    if (market && symbol) {
      fetchData();
    }
  }, [market, symbol]);
  
  if (loading) {
    return (
      <div className={compact ? '' : 'p-4 rounded-xl bg-white/5 border border-white/10'}>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={compact ? '' : 'p-4 rounded-xl bg-white/5 border border-white/10'}>
        <div className="flex items-center gap-2 text-gray-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }
  
  if (!data?.real_metrics) return null;
  
  const { real_metrics, nominal_metrics } = data;
  const { real_pe, real_roe, market_cap_usd, earnings_yield_spread, real_valuation_score, inflation_context } = real_metrics;
  
  // Compact version for screener table
  if (compact) {
    return (
      <div className="flex items-center gap-3">
        {real_pe?.real_pe && (
          <div className="text-center">
            <div className="text-xs text-gray-500">Real P/E</div>
            <MetricBadge value={real_pe.real_pe} type="pe" />
          </div>
        )}
        {real_roe?.real_roe && (
          <div className="text-center">
            <div className="text-xs text-gray-500">Real ROE</div>
            <MetricBadge value={real_roe.real_roe} type="roe" />
          </div>
        )}
        {real_valuation_score && (
          <div className="text-center">
            <div className="text-xs text-gray-500">Score</div>
            <MetricBadge value={real_valuation_score} type="score" />
          </div>
        )}
      </div>
    );
  }
  
  // Full widget version
  return (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-500" />
          <h3 className="text-white font-medium">Métriques Ajustées</h3>
        </div>
        <MetricTooltip metric="realValuationScore">
          <div className={`px-3 py-1 rounded-full text-sm font-medium border ${
            real_valuation_score >= 75 ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' :
            real_valuation_score >= 50 ? 'text-amber-400 border-amber-500/30 bg-amber-500/10' :
            'text-red-400 border-red-500/30 bg-red-500/10'
          }`}>
            Score: {real_valuation_score || '—'}/100
          </div>
        </MetricTooltip>
      </div>
      
      {/* Inflation context */}
      {inflation_context && (
        <div className="mb-4 p-3 rounded-lg bg-black/20 border border-white/5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Inflation {market}</span>
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{inflation_context.current}%</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                inflation_context.trend === 'rising' ? 'bg-red-500/20 text-red-400' :
                inflation_context.trend === 'falling' ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {inflation_context.trend === 'rising' ? '↑ Hausse' :
                 inflation_context.trend === 'falling' ? '↓ Baisse' : '→ Stable'}
              </span>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            vs cible {inflation_context.target}% | Source: {inflation_context.source}
          </div>
        </div>
      )}
      
      {/* Metrics */}
      <div className="space-y-1">
        <MetricRow
          label="P/E Ratio"
          tooltipKey="realPE"
          icon={TrendingUp}
          nominal={nominal_metrics?.pe}
          real={real_pe?.real_pe}
          type="pe"
        />
        
        <MetricRow
          label="ROE"
          tooltipKey="realROE"
          icon={Percent}
          nominal={nominal_metrics?.roe}
          real={real_roe?.real_roe}
          unit="%"
          type="roe"
        />
        
        {earnings_yield_spread?.spread !== null && (
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <MetricTooltip metric="earningsYieldSpread">
              <div className="flex items-center gap-2 cursor-help">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-400">Spread Rendement</span>
                <HelpCircle className="w-3 h-3 text-gray-600" />
              </div>
            </MetricTooltip>
            
            <div className="flex items-center gap-2">
              <MetricBadge value={earnings_yield_spread.spread} type="spread" />
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                earnings_yield_spread.attractiveness === 'very_attractive' || earnings_yield_spread.attractiveness === 'attractive'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : earnings_yield_spread.attractiveness === 'fair'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {earnings_yield_spread.attractiveness === 'very_attractive' ? 'Très attractif' :
                 earnings_yield_spread.attractiveness === 'attractive' ? 'Attractif' :
                 earnings_yield_spread.attractiveness === 'fair' ? 'Correct' : 'Cher'}
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Market Cap USD */}
      {market_cap_usd?.display && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <MetricTooltip metric="marketCapUSD">
            <div className="flex items-center justify-between cursor-help">
              <span className="text-sm text-gray-400">Cap. boursière (USD)</span>
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{market_cap_usd.display}</span>
                <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded">
                  {market_cap_usd.size_category?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </MetricTooltip>
        </div>
      )}
      
      {/* Explanation */}
      <div className="mt-4 text-xs text-gray-500">
        Métriques ajustées pour l'inflation locale ({inflation_context?.current}%) vs USA (2%)
      </div>
    </div>
  );
};

// Export compact components for screener use
export const RealPECell = ({ value, nominal }) => {
  if (!value) return <span className="text-gray-500">—</span>;
  
  const delta = nominal ? ((value - nominal) / nominal) * 100 : null;
  
  return (
    <div className="text-sm">
      <span className={`font-medium ${value < 15 ? 'text-emerald-400' : value < 25 ? 'text-amber-400' : 'text-red-400'}`}>
        {value.toFixed(1)}
      </span>
      {delta && Math.abs(delta) > 1 && (
        <span className={`ml-1 text-xs ${delta > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
          ({delta > 0 ? '+' : ''}{delta.toFixed(0)}%)
        </span>
      )}
    </div>
  );
};

export const RealROECell = ({ value, nominal }) => {
  if (!value) return <span className="text-gray-500">—</span>;
  
  return (
    <span className={`font-medium ${value > 15 ? 'text-emerald-400' : value > 8 ? 'text-amber-400' : 'text-red-400'}`}>
      {value.toFixed(1)}%
    </span>
  );
};

export const QualityScoreCell = ({ value }) => {
  if (!value) return <span className="text-gray-500">—</span>;
  
  return (
    <div className="flex items-center gap-1">
      <div className={`w-8 h-2 rounded-full overflow-hidden bg-white/10`}>
        <div 
          className={`h-full ${value >= 75 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className={`text-xs font-medium ${value >= 75 ? 'text-emerald-400' : value >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
        {value}
      </span>
    </div>
  );
};

export default RealMetricsWidget;
